// ============================================================
// A BROTHER'S FACTS (Squad Mode) - what every verb and every plate plans
// from: the brother, his kill-team's record and pool, his brothers on the
// stage and the whole table, and how far apart two people stand.
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant } from '../roster/types';
import { teamOf } from '../roster/team';
import { groundDistance } from '../battlefield/measure';
import { brothersIn, isBrother, type KillTeams } from './record';
import type { Pool } from './pool';
import type { Brothers } from './acts';
import type { DistanceTo } from './eligibility';

export type Brother = Brothers & { tag: string };

/** Metres across the ground between two markers; null when either is not on the map (D22: then in range). */
export const distanceIn =
  (tokens: Readonly<Record<string, { x: number; z: number }>>): DistanceTo =>
  (a, b) =>
    tokens[a] && tokens[b] ? groundDistance({ ...tokens[a], y: 0 }, { ...tokens[b], y: 0 }) : null;

/** This combatant as a brother of his kill-team, or null when he is none (or his team is no kill-team). */
export function brotherFacts(
  roster: readonly Combatant[],
  teams: KillTeams,
  pools: Readonly<Record<string, Pool>> | null,
  id: string,
  distance: DistanceTo,
): Brother | null {
  const brother = roster.find((c) => c.id === id);
  const tag = brother && teamOf(brother)?.tag;
  if (!brother || !tag || !isBrother(brother, tag)) return null;
  const kt = teams.killTeams[tag];
  if (!kt) return null;
  const pool = pools?.[tag] ?? null;
  return { brother, tag, kt, pool, here: brothersIn(roster, tag), roster, distance };
}
